/**
 * Marker Cache Manager
 * Manages loaded markers across viewport changes to avoid re-fetching same data
 */
class MarkerCache {
  /**
   * @param {number} maxSize - Maximum number of markers to cache
   */
  constructor(maxSize = 2000) {
    this.cache = new Map(); // id -> marker data
    this.viewportHistory = []; // Track loaded viewports
    this.maxSize = maxSize;
  }

  /**
   * Add markers to cache
   * @param {Array} markers - Array of marker objects
   */
  addMarkers(markers) {
    markers.forEach(marker => {
      if (!this.cache.has(marker.id)) {
        this.cache.set(marker.id, marker);
      }
    });

    // Cleanup if exceeds max size
    if (this.cache.size > this.maxSize) {
      this.cleanup();
    }
  }

  /**
   * Add grid data to cache (detailed property data)
   * @param {Array} gridData - Array of grid/property objects
   */
  addGridData(gridData) {
    gridData.forEach(item => {
      const existingMarker = this.cache.get(item.id);
      if (existingMarker) {
        // Update with detailed grid data
        this.cache.set(item.id, { ...existingMarker, ...item });
      } else {
        // Add as new entry if doesn't exist
        this.cache.set(item.id, item);
      }
    });
  }

  /**
   * Get all markers within a viewport
   * @param {Object} viewport - { neLat, neLng, swLat, swLng }
   * @returns {Array} - Markers within viewport
   */
  getMarkersInViewport(viewport) {
    const { neLat, neLng, swLat, swLng } = viewport;
    const markersInView = [];

    this.cache.forEach(marker => {
      const lat = parseFloat(marker.location.lat);
      const lng = parseFloat(marker.location.lng);

      if (lat >= swLat && lat <= neLat && lng >= swLng && lng <= neLng) {
        markersInView.push(marker);
      }
    });

    return markersInView;
  }

  /**
   * Check if viewport has been loaded before
   * Uses 80% overlap threshold to determine if viewport is "loaded"
   * @param {Object} viewport - { neLat, neLng, swLat, swLng }
   * @param {number} zoomLevel - Current zoom level
   * @returns {boolean} - True if viewport already loaded
   */
  isViewportLoaded(viewport, zoomLevel) {
    return this.viewportHistory.some(vh =>
      this.viewportOverlaps(vh.viewport, viewport) &&
      vh.zoom === zoomLevel
    );
  }

  /**
   * Record that a viewport has been loaded
   * @param {Object} viewport - { neLat, neLng, swLat, swLng }
   * @param {number} zoomLevel - Current zoom level
   */
  recordViewport(viewport, zoomLevel) {
    this.viewportHistory.push({
      viewport,
      zoom: zoomLevel,
      timestamp: Date.now()
    });

    // Keep only recent 20 viewports to limit memory usage
    if (this.viewportHistory.length > 20) {
      this.viewportHistory.shift();
    }
  }

  /**
   * Check if two viewports overlap significantly
   * @param {Object} vp1 - First viewport
   * @param {Object} vp2 - Second viewport
   * @returns {boolean} - True if overlap > 80%
   */
  viewportOverlaps(vp1, vp2) {
    // Calculate overlap dimensions
    const latOverlap = Math.max(
      0,
      Math.min(vp1.neLat, vp2.neLat) - Math.max(vp1.swLat, vp2.swLat)
    );
    const lngOverlap = Math.max(
      0,
      Math.min(vp1.neLng, vp2.neLng) - Math.max(vp1.swLng, vp2.swLng)
    );

    // Calculate areas
    const vp1Area = (vp1.neLat - vp1.swLat) * (vp1.neLng - vp1.swLng);
    const overlapArea = latOverlap * lngOverlap;

    // Return true if overlap is > 80%
    return overlapArea / vp1Area > 0.8;
  }

  /**
   * Cleanup old markers (LRU-style)
   * Removes oldest 20% of markers when cache is full
   */
  cleanup() {
    const entriesToRemove = this.cache.size - Math.floor(this.maxSize * 0.8);
    const entries = Array.from(this.cache.entries());

    // Remove oldest entries (simple FIFO for now)
    for (let i = 0; i < entriesToRemove; i++) {
      this.cache.delete(entries[i][0]);
    }

    console.log(`[MarkerCache] Cleaned up ${entriesToRemove} markers. Cache size now: ${this.cache.size}`);
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.viewportHistory = [];
    console.log('[MarkerCache] Cache cleared');
  }

  /**
   * Get all cached markers
   * @returns {Array} - All markers in cache
   */
  getAllMarkers() {
    return Array.from(this.cache.values());
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache stats
   */
  getStats() {
    return {
      markerCount: this.cache.size,
      maxSize: this.maxSize,
      usagePercent: Math.round((this.cache.size / this.maxSize) * 100),
      viewportHistoryCount: this.viewportHistory.length
    };
  }
}

export default MarkerCache;
